import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useIsClient } from "usehooks-ts";

import { TeamSectionsPopover } from "@/components/doc/left-sidebar/middle-menu/TeamSectionsPopover";
import { PersonalDocumentTreePopover } from "@/components/doc/left-sidebar/personal-document-tree/PersonalDocumentTreePopover";
import { TeamDropdown } from "@/components/doc/left-sidebar/team-dropdown/TeamDropdown";
import { NotificationsPopover } from "@/components/doc/left-sidebar/top-menu/NotificationsPopover";
import { SearchButton } from "@/components/doc/left-sidebar/top-menu/SearchButton";
import { SidebarTabs } from "@/components/doc/left-sidebar/top-menu/SidebarTabs";
import { TOP_MENU_BUTTON_CLASS_NAME } from "@/components/doc/left-sidebar/top-menu/topMenuButtonStyles";
import { SearchInput } from "@/components/search/SearchInput";
import { Sidebar } from "@/components/sidebar";
import * as Skeleton from "@/components/skeleton/Skeleton";
import { useStore } from "@/store";
import { keyMatcher } from "@/utils/keyMatcher";
import { twMerge } from "@/utils/twMerge";
import { useShowPublicProfile } from "@/utils/useShowPublicProfile";

export const TopMenu = observer(function TopMenu() {
  const { view, auth, search } = useStore();
  const isClient = useIsClient();
  const showPublicProfile = useShowPublicProfile();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) {
        return;
      }
      if (keyMatcher("Mod+Slash", e)) {
        e.preventDefault();

        if (search.isOpen) {
          search.close();
        } else {
          search.open();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [search, view]);

  if (!isClient) {
    return (
      <Sidebar.Group>
        <Skeleton.Root>
          <Sidebar.GroupSection className="mt-0 p-0">
            <Skeleton.Block
              className={twMerge("h-[38px]", view.isLeftSidebarCollapsed && "h-[176px]")}
            />
          </Sidebar.GroupSection>
        </Skeleton.Root>
      </Sidebar.Group>
    );
  }

  if (auth.isLoggedIn) {
    if (showPublicProfile && !view.isLeftSidebarCollapsed && search.isOpen) {
      return <SearchInput />;
    }

    const renderTabs = () => {
      if (view.isLeftSidebarCollapsed) {
        return (
          <>
            <PersonalDocumentTreePopover />
            <TeamSectionsPopover />
          </>
        );
      }
      if (showPublicProfile) {
        return <SearchButton className={TOP_MENU_BUTTON_CLASS_NAME} />;
      }
      return <SidebarTabs />;
    };

    return (
      <Sidebar.Group
        className={
          view.isLeftSidebarCollapsed ? "flex-col justify-between gap-2" : "p-1 gap-[22px]"
        }
      >
        {renderTabs()}
        <NotificationsPopover collapsed={view.isLeftSidebarCollapsed} />
        <TeamDropdown
          avatarOnly
          withArrow={view.leftSidebarState === "full"}
          collapsed={view.isLeftSidebarCollapsed}
          className={view.isLeftSidebarCollapsed ? undefined : "ml-auto"}
          anchor={
            view.isLeftSidebarCollapsed ? { to: "right start", gap: 14, padding: 10 } : undefined
          }
        />
      </Sidebar.Group>
    );
  }

  return null;
});
